import { useCallback, useEffect, useState } from 'react';
import axios from '../../../axiosConfig';

/**
 * Profit & Loss data for an arbitrary date range. Reuses /finance/summary
 * (which already aggregates revenue / COGS / expenses / other income +
 * per-category breakdowns), just bound to a from/to window instead of
 * the named period shortcuts the Overview tab uses.
 *
 * Pass `from` + `to` as ISO date strings (YYYY-MM-DD is fine).
 */
export default function usePnl({ from, to } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPnl = useCallback(async (rangeFrom, rangeTo) => {
    if (!rangeFrom || !rangeTo) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get('/finance/summary', {
        params: { from: rangeFrom, to: rangeTo },
      });
      setData(res.data);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load P&L';
      setError(msg);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPnl(from, to);
  }, [from, to, fetchPnl]);

  return { data, loading, error, refetch: () => fetchPnl(from, to) };
}
